import { environment } from '../../../environments/environment'

export const measurementList = [
  { headerName: '', parent: 'ME-PTR', group: 'measurements' },
  {
    headerName: 'Diagnostic thresholds',
    parent: 'ME-THR',
    group: 'measurements',
  },
  {
    headerName: 'Calculated values',
    parent: 'ME-CS',
    group: 'measurements',
    subGroup: 'clinical_scores',
  },
  { headerName: 'Program checks', parent: 'ME-PDI', group: 'measurements' },
  { headerName: 'Questionnaires', parent: 'ME-CAL', group: 'measurements' },
  { headerName: 'Other', parent: 'ME-OTHR', group: 'measurements' },
]

export const patientInfoList = [
  {
    headerName: 'Referral',
    parent: 'MD-PATIENT-INFO',
    group: 'medications',
    subGroup: 'patient_toxicities',
  },
  {
    headerName: 'Clinical team',
    parent: 'MD-CLI-TEAM',
    group: 'medications',
    subGroup: 'patient_toxicities',
  },
  {
    headerName: 'Clinic visit',
    parent: 'MD-CLI-VISIT',
    group: 'medications',
    subGroup: 'patient_toxicities',
  },
  {
    headerName: 'Pharmacy',
    parent: 'MD-PHARMACY',
    group: 'medications',
    subGroup: 'patient_pharmacies',
  },
]

export const treatmentsList = [
  { headerName: 'Care plans', parent: 'MD-PRMD', group: 'medications' },
  {
    headerName: 'Toxicities',
    parent: 'MD-TOXEVAL',
    group: 'medications',
    subGroup: 'patient_toxicities',
  },
  {
    headerName: 'Lifestyle plans',
    parent: 'MD-LS',
    group: 'medications',
    subGroup: 'lifestyles',
  },
  {
    headerName: 'Interventions',
    parent: 'MD-EXP',
    group: 'medications',
    subGroup: 'experiments',
  },
  {
    headerName: 'Monitors',
    parent: 'MD-PRMT',
    group: 'medications',
    subGroup: 'monitors',
  },
  {
    headerName: 'Symptom evaluations',
    parent: 'MD-SXEVAL',
    group: 'medications',
    subGroup: 'symptom_evaluations',
  },
  {
    headerName: 'Imported medications',
    parent: 'MD-RECON',
    group: 'medications',
    subGroup: 'non_synced_medications',
  },
  {
    headerName: 'Other medications',
    parent: 'MD-IMMD',
    group: 'medications',
    subGroup: 'other_medications',
  },
  {
    headerName: 'Medication restrictions',
    parent: 'MD-PRM',
    group: 'medications',
    subGroup: 'med_intolerances',
  },
]
export const rpmList = [
  {
    headerName: 'RPM Billing',
    parent: 'RPM-BILLING',
    group: 'rpm',
  },
]

export const conditionsList = [
  {
    headerName: 'Diagnosis plans',
    parent: 'DIA-PLAN',
    group: 'diagnosis',
  },
    {
    headerName: 'AI Review',
    parent: 'AI-REVIEW',
    group: 'ai-review',
  },
  {
    headerName: 'Tests',
    parent: 'DIA-TEST',
    group: 'diagnosis',
  },
  {
    headerName: 'Clinical assessments',
    parent: 'CO-DIA',
    group: 'conditions',
  },
  {
    headerName: 'Problems for evaluation',
    parent: 'CO-PRB',
    group: 'conditions',
    subGroup: 'problems',
  },
  {
    headerName: 'Relevant comorbidities',
    parent: 'CO-PSC',
    group: 'conditions',
  },
  {
    headerName: 'ICD10 codes',
    parent: 'CO-ICD10',
    group: 'conditions',
    subGroup: 'icd10_codes',
  },
  {
    headerName: 'Other conditions',
    parent: 'CO-OC',
    group: 'conditions',
    subGroup: 'other_conditions',
  },
]

// ---------------------------------------------------------------------------
// Deployment surface profile
// ---------------------------------------------------------------------------
// A profile declares which patient-detail sections a deployment suppresses, by
// `parent` code. This table is the single answer to "what does this client see?"
//
// AIDA (amyloidosis evaluation, nurse-operated) is a read-mostly deployment:
// prioritized data plus a passive test tracker rather than an active CDS
// surface, so the sections that ask a nurse to author a plan are suppressed.
//
// Declare suppression HERE, never as an inline `Helper.isAIDA()` filter at a
// call site. The section lists above have two consumers — detail-container and
// pending-items — and an inline filter in one silently exempts the other. That
// is exactly how 'AI Review' came to be hidden on the patient page while still
// listed in the Pending items dialog.

export type SurfaceProfile = 'default' | 'aida'

const SUPPRESSED_SECTIONS: Record<SurfaceProfile, string[]> = {
  // Stock provider deployment. Referral and Clinic visit are AIDA intake
  // surfaces and have never been part of it.
  default: [
    'MD-PATIENT-INFO', // Referral
    'MD-CLI-VISIT',    // Clinic visit
  ],

  // AI Review is deliberately NOT suppressed here. Stock AIDA hid it; the demo
  // client asked for AI, and the AI-extracted diagnosis/measurement review IS
  // the prioritized-data story, so it is visible in this profile on purpose.
  aida: [
    'MD-PRMT',     // Monitors
    'MD-LS',       // Lifestyle plans
    'MD-PRM',      // Medication restrictions
    // Experiments is deliberately NOT suppressed — cut with the item-8 list, then
    // restored on RD's call (2026-08-08).
    'MD-SXEVAL',   // Symptom evaluations
    'MD-PHARMACY', // Pharmacy
    'CO-PRB',      // Problems for evaluation
    'CO-ICD10',    // ICD10 codes
    'CO-OC',       // Other conditions
  ],
}

export const currentSurfaceProfile = (): SurfaceProfile =>
  environment.aida.isEnabled ? 'aida' : 'default'

// Named controls — buttons and banners a profile hides. The section lists above
// are keyed by `parent`; these are not sections, so they need their own set
// rather than a second mechanism sprinkled across components.
const SUPPRESSED_CONTROLS: Record<SurfaceProfile, string[]> = {
  default: [],

  aida: [
    // Bulk Elation lab drafting — this client has no write path from us to their
    // EHR, so the action cannot complete.
    'order_all_labs',

    // Both per-test order actions. Same reason as order_all_labs: no write path from us
    // to their EHR, so an Elation draft cannot be created. "Select site" goes with it —
    // its only purpose is to name the facility an order is sent to, and site
    // administration is out of this deployment entirely.
    'test_order_in_elation',
    'test_select_site',

    // Referring as a free-text field on the patient/referral cards. It is now a star on
    // the care team membership (is_referring), which is FK-backed — the free-text entry
    // saved whatever was typed rather than a registry selection.
    'referring_free_text',

    // Associate / Unassociate on a care team member. The distinction only means
    // something when providers arrive from a Surescripts import as an unattached
    // pile of names, some of which get attached to the patient. This client enters
    // every member by hand, so a member is associated the moment it exists — and
    // the unassociated default made a just-added provider render as ○ (RD 2026-08-08).
    'care_team_association',
  ],
}

export const isControlVisible = (
  control: string,
  profile: SurfaceProfile = currentSurfaceProfile(),
): boolean => !SUPPRESSED_CONTROLS[profile].includes(control)

// The task lists seed their Hospital filter with the billing cohorts, so a navigator opens
// straight into their own book of work. AIDA has none of those cohorts, so the seed matched
// nothing and every task page opened on "Data not available" with three chips the client
// does not recognise (RD 2026-08-08). Returns the stock seed unchanged for other profiles,
// so the per-page cohort lists keep whatever they already had.
export const taskHospitalPrefilterFor = (
  stockValue: string,
  profile: SurfaceProfile = currentSurfaceProfile(),
): string => (profile === 'aida' ? '' : stockValue)

// Drop the sections this profile suppresses. Order is preserved, so callers can
// still apply their own program-type filtering and ordering on the result.
export const forProfile = <T extends { parent: string }>(
  list: T[],
  profile: SurfaceProfile = currentSurfaceProfile(),
): T[] => {
  const suppressed = SUPPRESSED_SECTIONS[profile]
  return (list || []).filter((item) => !suppressed.includes(item.parent))
}
